/**
 * Product image utilities
 * Provides a default placeholder SVG and helper for resolving product image URLs.
 */

// Inline SVG data URI placeholder for missing product images.
// Renders a neutral gray box with a simple bag/shopping icon and "No Image" text.
export const PRODUCT_PLACEHOLDER = `data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">
    <rect width="400" height="400" fill="#f0f0f0"/>
    <g transform="translate(200,180)" fill="none" stroke="#bbb" stroke-width="6" stroke-linecap="round" stroke-linejoin="round">
      <path d="M-40 10 L-50 60 L50 60 L40 10 Z"/>
      <path d="M-25 10 C-25-20 25-20 25 10"/>
      <line x1="0" y1="28" x2="0" y2="42"/>
      <line x1="-7" y1="35" x2="7" y2="35"/>
    </g>
    <text x="200" y="270" text-anchor="middle" font-family="Arial,sans-serif" font-size="16" fill="#999">No Image</text>
  </svg>`
)}`;

/**
 * Resolves the display URL for a product image string.
 * - Full URLs (http/https) and absolute paths (/) are returned as-is.
 * - Relative filenames are prefixed with /assets/images/products/.
 * - Falsy values return the inline SVG placeholder.
 *
 * @param {string|null|undefined} image - raw image value from product data
 * @returns {string} resolved image URL or placeholder data URI
 */
export const getProductImageSrc = (image) => {
  if (!image) return PRODUCT_PLACEHOLDER;
  if (image.startsWith("http") || image.startsWith("/") || image.startsWith("data:")) {
    return image;
  }
  return `/assets/images/products/${image}`;
};

/**
 * onError handler factory for <img> elements.
 * Sets the image src to the SVG placeholder on load failure.
 * Removes itself after firing once to avoid infinite loops.
 *
 * Usage: <img onError={handleProductImageError} ... />
 */
export const handleProductImageError = (e) => {
  e.target.onerror = null; // prevent infinite loop
  e.target.src = PRODUCT_PLACEHOLDER;
};
